import { useT } from '../i18n';
import { type NavView } from '../components/Layout';
import { usePWAInstall } from '../hooks/usePWAInstall';

/**
 * InstallApp.tsx — Página "Como instalar" (PWA) — Sprint 7 (#S7-045).
 *
 * Explica, em linguagem não-técnica (pt-BR), como instalar a webapp Aura Nota
 * como um aplicativo nos principais navegadores (Chrome, Edge, Safari, Firefox).
 *
 * Recursos:
 *  - Botão "Instalar agora" que dispara `beforeinstallprompt` quando suportado
 *    (capturado globalmente pelo hook usePWAInstall — Issue #287).
 *  - Detecção de app já instalado (display-mode: standalone).
 *  - Instruções manuais por navegador quando não há suporte a instalação automática.
 *  - Seção "Por que instalar?" com benefícios.
 */

interface InstallAppProps {
  onNavigate?: (view: NavView) => void;
}

function InstallApp({ onNavigate }: InstallAppProps) {
  const t = useT();

  // Issue #287: usa o hook global que captura beforeinstallprompt no nível do App.
  const { canInstall, isInstalled, installOutcome, promptInstall } = usePWAInstall();

  const handleInstallClick = promptInstall;

  return (
    <div className="space-y-10">
      {/* ----------------------------------------------------------------- */}
      {/* Hero                                                              */}
      {/* ----------------------------------------------------------------- */}
      <section className="rounded-2xl bg-gradient-to-br from-brand-700 to-brand-900 px-8 py-12 text-center text-white">
        <div className="mx-auto mb-4 text-5xl">📲</div>
        <h1 className="text-3xl font-bold sm:text-4xl">{t('install.title')}</h1>
        <p className="mx-auto mt-3 max-w-2xl text-brand-100">{t('install.subtitle')}</p>

        {/* Botão "Instalar agora" / mensagem "já instalado" / sem suporte */}
        <div className="mt-6">
          {isInstalled ? (
            <div className="inline-flex items-center gap-2 rounded-full bg-green-500 px-6 py-3 text-base font-semibold text-white">
              <span aria-hidden>✅</span>
              {t('install.installed')}
            </div>
          ) : canInstall ? (
            <button
              type="button"
              onClick={handleInstallClick}
              className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-base font-semibold text-brand-800 shadow hover:bg-brand-50"
            >
              <span aria-hidden>⬇️</span>
              {t('install.button')}
            </button>
          ) : installOutcome === 'dismissed' ? null : (
            <p className="mx-auto max-w-2xl rounded-md bg-white/10 px-4 py-3 text-sm text-brand-50">
              {t('install.all_browsers_can_install')}
            </p>
          )}
        </div>
      </section>

      {/* ----------------------------------------------------------------- */}
      {/* Por que instalar?                                                */}
      {/* ----------------------------------------------------------------- */}
      <section>
        <h2 className="mb-6 text-2xl font-bold text-brand-800">{t('install.why_install')}</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <BenefitCard icon="⚡" text={t('install.benefit_fast')} />
          <BenefitCard icon="🖥️" text={t('install.benefit_fullscreen')} />
          <BenefitCard icon="🔔" text={t('install.benefit_notifications')} />
          <BenefitCard icon="📱" text={t('install.benefit_native')} />
        </div>
      </section>

      {/* ----------------------------------------------------------------- */}
      {/* Instruções por navegador                                         */}
      {/* ----------------------------------------------------------------- */}
      <section>
        <h2 className="mb-2 text-2xl font-bold text-brand-800">{t('install.instructions_title')}</h2>
        <p className="mb-6 text-brand-600">{t('install.instructions_intro')}</p>

        <div className="grid gap-6 lg:grid-cols-2">
          <BrowserCard
            badge="🟢"
            title={t('install.chrome_android')}
            steps={[
              t('install.chrome_android.step1'),
              t('install.chrome_android.step2'),
              t('install.chrome_android.step3'),
            ]}
          />
          <BrowserCard
            badge="🟢"
            title={t('install.chrome_desktop')}
            steps={[
              t('install.chrome_desktop.step1'),
              t('install.chrome_desktop.step2'),
              t('install.chrome_desktop.step3'),
            ]}
          />
          <BrowserCard
            badge="🔵"
            title={t('install.edge_desktop')}
            steps={[
              t('install.edge_desktop.step1'),
              t('install.edge_desktop.step2'),
              t('install.edge_desktop.step3'),
            ]}
          />
          <BrowserCard
            badge="🧭"
            title={t('install.safari_ios')}
            steps={[
              t('install.safari_ios.step1'),
              t('install.safari_ios.step2'),
              t('install.safari_ios.step3'),
            ]}
          />
          <BrowserCard
            badge="🧭"
            title={t('install.safari_macos')}
            steps={[
              t('install.safari_macos.step1'),
              t('install.safari_macos.step2'),
              t('install.safari_macos.step3'),
            ]}
          />
          <BrowserCard
            badge="🦊"
            title={t('install.firefox_android')}
            steps={[
              t('install.firefox_android.step1'),
              t('install.firefox_android.step2'),
              t('install.firefox_android.step3'),
            ]}
          />
          <BrowserCard
            badge="🦊"
            title={t('install.firefox_desktop')}
            steps={[
              t('install.firefox_desktop.step1'),
              t('install.firefox_desktop.step2'),
              t('install.firefox_desktop.step3'),
            ]}
            note={t('install.firefox_desktop.note')}
          />
        </div>
      </section>

      {/* ----------------------------------------------------------------- */}
      {/* Nota de suporte / contato                                        */}
      {/* ----------------------------------------------------------------- */}
      <section className="rounded-xl border border-brand-200 bg-brand-50 px-6 py-5 text-center text-brand-700">
        <p className="font-medium">{t('install.contact_support')}</p>
      </section>

      {/* ----------------------------------------------------------------- */}
      {/* Footer                                                            */}
      {/* ----------------------------------------------------------------- */}
      <footer className="border-t border-brand-200 pt-6 text-center text-sm text-brand-500">
        <p>{t('install.compatibility_note')}</p>
        <button
          type="button"
          onClick={() => onNavigate?.('home')}
          className="mt-3 text-brand-700 underline hover:text-brand-900"
        >
          {t('install.back_home')}
        </button>
      </footer>
    </div>
  );
}

function BenefitCard({ icon, text }: { icon: string; text: string }) {
  return (
    <div className="rounded-xl border border-brand-200 bg-white p-5 text-center shadow-sm">
      <div className="mb-2 text-3xl" aria-hidden>{icon}</div>
      <p className="text-sm font-medium text-brand-700">{text}</p>
    </div>
  );
}

function BrowserCard({
  badge,
  title,
  steps,
  note,
}: {
  badge: string;
  title: string;
  steps: string[];
  note?: string;
}) {
  return (
    <div className="rounded-xl border border-brand-200 bg-white p-6 shadow-sm">
      <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-brand-800">
        <span aria-hidden>{badge}</span>
        {title}
      </h3>
      <ol className="list-inside list-decimal space-y-2 text-sm text-brand-700">
        {steps.map((step, i) => (
          <li key={i}>{step}</li>
        ))}
      </ol>
      {note && (
        <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">{note}</p>
      )}
    </div>
  );
}

export default InstallApp;
